// Display-normalization for LMS (Live Media Service) ingest/output endpoints.
//
// Ingest is always advertised on a single fixed host, and playback/output URLs
// are shown as path-only (the scheme://host is dropped so the path can be served
// under whichever delivery host the customer fronts it with).

/** The one host all LMS ingest is advertised on. */
export const LMS_INGEST_HOST = 'vnetwork.lms.jns.veloceed.com';

/**
 * Delivery-domain prefix for playback/output URLs. The path is provider-fixed but
 * the customer fronts it with their own delivery domain, so we show a placeholder
 * they substitute.
 */
export const LMS_DELIVERY_DOMAIN = 'https://<DELIVERY-DOMAIN>';

// Matches the "scheme://host" prefix of an absolute URL, capturing the scheme
// (group 1) and whatever path/query/hash follows the host (group 2).
const ABSOLUTE_URL = /^([a-zA-Z][\w+.-]*:\/\/)[^/?#]*([/?#].*)?$/;

/**
 * Swap the host of an absolute URL for the fixed LMS ingest host, keeping the
 * scheme and path intact (e.g. `rtmp://old/app/key` → `rtmp://vnetwork…/app/key`).
 * A bare host or empty value collapses to the fixed host.
 */
export function withLmsIngestHost(url: string | undefined | null): string {
  if (!url) return LMS_INGEST_HOST;
  const m = url.match(ABSOLUTE_URL);
  if (!m) return LMS_INGEST_HOST; // bare host / non-URL → just the host
  return `${m[1]}${LMS_INGEST_HOST}${m[2] ?? ''}`;
}

/** Reduce a value to its path only, dropping any `scheme://host`. */
function toPath(pathOrUrl: string): string {
  const m = pathOrUrl.match(ABSOLUTE_URL);
  if (m) return m[2] ?? '/';
  return pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
}

/**
 * Build a playback/output URL as `https://<DELIVERY-DOMAIN>` + path — the
 * provider host is dropped and the delivery-domain placeholder prepended so the
 * customer serves it under their own delivery domain.
 */
export function toLmsOutputUrl(pathOrUrl: string | undefined | null): string {
  if (!pathOrUrl) return '';
  return `${LMS_DELIVERY_DOMAIN}${toPath(pathOrUrl)}`;
}

/**
 * Split a push URL at its app segment so the base (`rtmp://host/<app>`) and the
 * stream key (everything after `/<app>/`, including any `?query` token) show in
 * separate fields. If the app segment isn't found, the whole URL is the base.
 */
export function splitPushUrl(
  url: string | undefined | null,
  appName: string | undefined | null,
): { base: string; key: string } {
  if (!url) return { base: '', key: '' };
  const marker = appName ? `/${appName}/` : '';
  const i = marker ? url.indexOf(marker) : -1;
  if (i === -1) return { base: url, key: '' };
  return { base: url.slice(0, i + marker.length - 1), key: url.slice(i + marker.length) };
}
